import { Component, OnDestroy, OnInit } from '@angular/core';
import { CmsComponentData } from '@spartacus/storefront';
import { NEVER, Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';
import { CmsFlexNavigationComponent, FeedBadgeType } from '../../../../core/model';
import { FeedFacade } from '../../../../core/user';
import { UserUtilService } from '../../../../core/user/services/user-util.service';
import { NavigationService } from '../navigation';
import { NavigationNode } from '../navigation/navigation-node.model';

@Component({
  selector: 'py-my-page-menu',
  templateUrl: './my-page-menu.component.html',
})
export class MyPageMenuComponent implements OnInit, OnDestroy {
  private subscription: Subscription = new Subscription();

  menuItems: Array<NavigationNode> = [];

  node$: Observable<NavigationNode> = this.navigationService.getNavigationNode(this.componentData.data$);
  notificationReturnedCount$: Observable<number>;
  notificationPendingCount$: Observable<number>;
  notificationShoppingListsWithDiscontinuedArticlesCount$: Observable<number>;
  notificationOrderCardsCount$: Observable<number>;
  notificationAreasOfUseCount$: Observable<number>;
  notificationUsersCount$: Observable<number>;

  constructor(
    protected componentData: CmsComponentData<CmsFlexNavigationComponent>,
    protected navigationService: NavigationService,
    protected feedService: FeedFacade,
    private userUtilService: UserUtilService
  ) {}

  ngOnInit() {
    this.initNotifcationCountObservables();
  }

  getNotificationCountForLink(styleClasses: string): Observable<number> {
    switch (styleClasses) {
      case 'approvals-link':
        return this.notificationPendingCount$;
      case 'orders-link':
        return this.notificationReturnedCount$;
      case 'shopping-lists-link':
        return this.notificationShoppingListsWithDiscontinuedArticlesCount$;
      case 'order-cards-admin-link':
        return this.notificationOrderCardsCount$;
      case 'areas-of-use-admin-link':
        return this.notificationAreasOfUseCount$;
      case 'users-link':
        return this.notificationUsersCount$;
      default:
        return NEVER;
    }
  }

  initNotifcationCountObservables(): void {
    const feedData$ = this.feedService.getFeedData().pipe(shareReplay({ bufferSize: 1, refCount: false }));

    this.notificationPendingCount$ = feedData$.pipe(
      filter((feedData) => !!feedData),
      map((feedData) => this.userUtilService.getCountForBadgeType(feedData.badges, FeedBadgeType.PENDING_APPROVAL_ORDERS))
    );

    this.notificationReturnedCount$ = feedData$.pipe(
      filter((feedData) => !!feedData),
      map((feedData) => this.userUtilService.getCountForBadgeType(feedData.badges, FeedBadgeType.RETURNED_ORDERS))
    );

    this.notificationShoppingListsWithDiscontinuedArticlesCount$ = feedData$.pipe(
      filter((feedData) => !!feedData),
      map((feedData) =>
        this.userUtilService.getCountForBadgeType(feedData.badges, FeedBadgeType.SHOPPING_LISTS_WITH_UNAVAILABLE_ARTICLES_COUNT)
      )
    );

    this.notificationOrderCardsCount$ = feedData$.pipe(
      filter((feedData) => !!feedData),
      map((feedData) =>
        this.userUtilService.getCountForBadgeType(feedData.badges, FeedBadgeType.ORDER_CARDS_WITH_UNAVAILABLE_PRODUCTS_COUNT)
      )
    );

    this.notificationAreasOfUseCount$ = feedData$.pipe(
      filter((feedData) => !!feedData),
      map((feedData) =>
        this.userUtilService.getCountForBadgeType(feedData.badges, FeedBadgeType.AREAS_OF_USE_UNAVAILABLE_PRODUCTS_COUNT)
      )
    );

    this.notificationUsersCount$ = feedData$.pipe(
      filter((feedData) => !!feedData),
      map(
        (feedData) =>
          this.userUtilService.getCountForBadgeType(feedData.badges, FeedBadgeType.NEW_USERS_COUNT) +
          this.userUtilService.getCountForBadgeType(feedData.badges, FeedBadgeType.LOCKED_OUT_USERS_COUNT)
      )
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
